import React, { useState } from "react";
import { CheckCircle, AlertCircle } from "lucide-react";

// ✅ Removed unused `onNext` prop — MultiStepForm renders the Submit button
//    outside this component, so the internal "Next" button was a dead no-op.
const StepDeclaration = ({ formData, setFormData }) => {
  const [touched, setTouched] = useState(false);

  const handleCheckbox = (e) => {
    setFormData({ ...formData, declarationAccepted: e.target.checked });
    setTouched(true);
  };

  const declarationError = touched && !formData.declarationAccepted;

  const sectionTitle = "text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3";
  const labelClass   = "text-sm text-gray-500";
  const valueClass   = "text-sm font-medium text-gray-800";
  const rowClass     = "flex justify-between py-1.5 border-b border-gray-50 last:border-b-0";

  return (
    <section className="w-full max-w-full sm:max-w-4xl mx-auto bg-white border border-gray-200 rounded-xl p-4 sm:p-6 md:p-8 space-y-6 overflow-x-hidden">
      <div>
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1">
          Section E: Declaration & Review
        </h2>
        <p className="text-gray-500 text-sm">
          Review all information carefully and confirm your declaration before submitting.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <h4 className={sectionTitle}>Personal Information</h4>
          <div className="space-y-0.5">
            {[
              { label: "Full Name",        value: formData.personal?.fullName },
              { label: "Gender",           value: formData.personal?.gender },
              { label: "Date of Birth",    value: formData.personal?.dateOfBirth },
              { label: "ID / Certificate", value: formData.personal?.identificationNumber },
              { label: "School Level",     value: formData.personal?.schoolLevel },
            ].map(({ label, value }) => (
              <div key={label} className={rowClass}>
                <span className={labelClass}>{label}</span>
                <span className={valueClass}>{value || "—"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <h4 className={sectionTitle}>Guardian Information</h4>
          <div className="space-y-0.5">
            {[
              { label: "Guardian Name",  value: formData.guardian?.guardianName },
              { label: "Guardian ID",    value: formData.guardian?.guardianId },
              { label: "Guardian Phone", value: formData.guardian?.guardianPhone },
              { label: "Ward",           value: formData.guardian?.ward },
              { label: "Village",        value: formData.guardian?.village },
            ].map(({ label, value }) => (
              <div key={label} className={rowClass}>
                <span className={labelClass}>{label}</span>
                <span className={valueClass}>{value || "—"}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:col-span-2">
          <h4 className={sectionTitle}>Uploaded Documents</h4>
          <div className="space-y-0.5">
            {[
              { label: "ID Document",       key: "idDocument" },
              { label: "Guardian Document", key: "guardianDocument" },
              { label: "Other Document",    key: "otherDocument" },
            ].map(({ label, key }) => (
              <div key={key} className={rowClass}>
                <span className={labelClass}>{label}</span>
                <span className={`text-sm ${formData.documents?.[key] ? "text-green-600 font-medium flex items-center gap-1" : "text-gray-400"}`}>
                  {formData.documents?.[key]
                    ? <><CheckCircle size={12} /> {formData.documents[key].name}</>
                    : "Not uploaded"
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`p-4 rounded-xl border-l-4 flex items-start gap-3 ${
        formData.declarationAccepted
          ? "bg-green-50 border-green-500"
          : declarationError
          ? "bg-red-50 border-red-400"
          : "bg-blue-50 border-primary"
      }`}>
        <input
          type="checkbox"
          id="declaration"
          checked={formData.declarationAccepted || false}
          onChange={handleCheckbox}
          className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0 cursor-pointer"
        />
        <label htmlFor="declaration" className="text-gray-700 text-sm cursor-pointer leading-relaxed">
          I declare that all information I have provided is <strong>true, correct and complete</strong>.
          I understand that providing false information may result in disqualification.
        </label>
      </div>

      {declarationError && (
        <p className="text-red-500 text-sm flex items-center gap-1.5">
          <AlertCircle size={14} /> You must accept the declaration to proceed.
        </p>
      )}
    </section>
  );
};

export default StepDeclaration;